import { useEffect, useRef, useState } from "react";
import { ExternalLink, RefreshCw, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function IdentityVerificationDialog({ open, onClose, onSubmitted, user }) {
  const { refreshUser } = useAuth();
  const [verification, setVerification] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const announced = useRef(false);
  const callbacks = useRef({ refreshUser, onSubmitted });
  callbacks.current = { refreshUser, onSubmitted };
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    const load = async () => {
      try {
        const { data } = await api.get("/me/identity/didit");
        if (cancelled) return;
        setVerification(data);
        if (data.status === "approved" && data.provider === "didit" && !announced.current) {
          announced.current = true;
          toast.success("Your ID has been automatically verified by Didit.");
          await callbacks.current.refreshUser?.();
          await callbacks.current.onSubmitted?.();
        }
      } catch (err) { if (!cancelled) setError(formatApiError(err)); }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [open]);
  if (!open) return null;
  const verified = verification?.status === "approved" && verification?.provider === "didit";
  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/me/identity/didit/session");
      if (data.verified) {
        await refreshUser?.();
        await onSubmitted?.();
        onClose();
        return;
      }
      const url = new URL(data.url);
      if (url.protocol !== "https:" || url.hostname !== "verify.didit.me") throw new Error("Invalid verification link");
      window.location.assign(url.href);
    } catch (err) { setError(formatApiError(err)); setBusy(false); }
  };
  const refresh = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/me/identity/didit/refresh");
      setVerification(data);
      await refreshUser?.();
      await onSubmitted?.();
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-black/75 p-4 py-6 backdrop-blur-sm sm:grid sm:place-items-center" onClick={onClose}>
      <section role="dialog" aria-modal="true" aria-labelledby="didit-dialog-title" onClick={(event) => event.stopPropagation()} className="mx-auto w-full max-w-lg rounded-2xl border border-sky-400/30 bg-[#0A130E] p-5 text-white shadow-2xl sm:p-6">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-300">Automatic ID verification</p>
            <h3 id="didit-dialog-title" className="mt-2 text-2xl font-black">Verify with Didit</h3>
          </div>
          <button type="button" aria-label="Close verification" onClick={onClose} className="rounded-full p-2 text-white/60 hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>
        <div className="mb-5 flex gap-3 rounded-xl border border-sky-300/20 bg-sky-300/5 p-4">
          <ShieldCheck className="h-7 w-7 shrink-0 text-sky-300" />
          <p className="text-sm leading-relaxed text-white/70">Have your ID ready. Didit checks your ID front and back, takes a live selfie, and matches it to your ID. You will return here when finished.</p>
        </div>
        <p className="mb-4 text-sm text-white/60">Your result updates automatically. A verified phone number is also required for withdrawals. An identity already used by another account cannot be approved.</p>
        {!user?.phone_verified && <p className="mb-4 text-sm text-amber-200">You still need to verify your phone number before withdrawing.</p>}
        {verification && <div className={`mb-4 rounded-xl border p-3 text-sm ${verified ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200" : "border-white/10 text-white/70"}`}>
          <strong>{verified ? "ID verified" : verification.status === "rejected" ? "Verification not approved" : verification.provider === "didit" ? "Verification in progress" : "Ready to verify"}</strong>
          {verification.note && !verified && <p className="mt-1">{verification.note}</p>}
        </div>}
        {verification && !verification.configured && !verified && <p className="mb-4 rounded-xl border border-amber-300/20 bg-amber-300/5 p-3 text-sm text-amber-100">Automatic verification is being connected. Please contact support or check back shortly.</p>}
        {error && <p role="alert" className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</p>}
        {verified ? <button onClick={onClose} className="w-full rounded-full bg-emerald-300 px-5 py-3 font-bold text-black">Done</button> : <button onClick={start} disabled={busy || !verification?.configured} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-sky-300 px-5 py-3 font-black text-black hover:bg-sky-200 disabled:opacity-50"><ExternalLink className="h-4 w-4" />{busy ? "Please wait..." : verification?.url ? "Continue verification" : "Start Didit verification"}</button>}
        {verification?.session_id && <button onClick={refresh} disabled={busy} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white/70 disabled:opacity-50"><RefreshCw className="h-4 w-4" />Check verification result</button>}
      </section>
    </div>
  );
}
